import { useTenantNavigate } from '@app-store'
import {
  DataTypeStore,
  EntityLookupSearchCriteria,
  IEntityFieldType,
  TableFilterLookupObjectBody,
  addToaster,
  getCategoryDropdownData,
  getEntityDataByEntityIdLayoutIdApiForFilter,
  hideLoader,
  setDialPad,
  showLoader,
  SORT_KEYS,
  getGenericEntityListingAPI,
  setLocalEntityObject,
  getAllEntities,
  IEntityType,
  SearchCriteria,
  getEntityDataByEntityIdApiForFilter,
  exportEntityDataByEntityIdLayoutIdApiForFilter,
  FieldDataByFieldNameType,
  formatDataObject,
  ToasterTypeValues,
  ToasterType,
  appendTenantIdToPath,
  getTenantIdFromWindow,
  openWithTenant,
} from '@app-store'
import {
  ACTION_ICONS_NAMESPACE,
  columnTypesConst,
  MODE_CONSTANTS,
  TableColumnType,
  TableFilterObjectBody,
} from '@ui-kit'
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  createContext,
  useCallback,
} from 'react'
import {
  checkFilterColumn,
  formatDataForListView,
  formatFilterConfig,
  transformLayoutToCols,
  wrappedGetStringOptionsForEntities,
} from '../../utils/listing.utils'
import {
  COMMUNICATION_SELECT_FIELDS,
  componentNames,
  correspondenceLogsPage,
  CUSTOM_SEPERATOR,
  ENTITY_ID,
  ENTITY_IMPORTANT_KEYS,
  ENTITY_NAME,
  ENTITY_TABLE_EXPORT_EVENT,
  HTTP_STATUS_CONSTANTS,
  HYBRID_LAYOUT_CONSTANTS,
  MIN_TABLE_HEIGHT,
  NAMESPACES,
} from '../../utils/constants'
import { useListingTableActions } from './ListingTableActions'
import { cloneDeep, isEmpty, isNil, startCase, uniqueId } from 'lodash'
import { hasPermission, hideUnauthorizedData } from '../../utils/permission'
import { RECORD_STATUS } from '../../constants/form.constants'
import { fetchDownloadUrlForSingleFIles } from '../../apis/fetchPresignedUrl.api'
import { store, useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  ListingTableProps,
  ListingTableRow,
} from '../../types/listingTable.types'
import { v4 as uuid } from 'uuid'
import PersistTable from '../../common/PersistTable'
import {
  getMigratedLayoutId,
  getMigrationStatus,
} from '../../utils/migration.utils'
import { removePhoneMasking } from '../../utils/form.utils'
import PaginationWithFilter from './PaginationWIthFilter'
import { FieldValues } from 'react-hook-form'
import { downloadCSV, parseErrorMessage } from '../../utils/helperFunctions'
import {
  ExportPayloadType,
  RelatedEntityType,
} from '../../types/correspondenceLogs.types'
import { Column, UiDataMapping } from '../../types/types'
import moment from 'moment'
import { isAxiosError } from 'axios'

interface DataContextType {
  refetchData: () => void
}

export const DataContext = createContext<DataContextType>({
  refetchData: () => null,
})

let related_entities: Record<string, { select: string[] }> = {}
let formatFilter: {
  filter_criteria: EntityLookupSearchCriteria[]
  related_entities: TableFilterLookupObjectBody
}

const ListingTableFromLayout: React.FC<
  ListingTableProps & {
    layout: NonNullable<ListingTableProps['layout']>
    allLayouts: NonNullable<ListingTableProps['allLayouts']>
  }
> = ({
  entityId,
  entityName,
  layout,
  tabIndex,
  apiFilter,
  setApiFilter,
  isMinHeight = true,
  caseId,
  isCaseView = false,
  ...props
}) => {
  const tableName = `listing-table-${props?.persistTableName ?? ''}`
  const tableColumns = useAppSelector(
    (state) => state?.tableColumn?.tableColumn?.[tableName]
  )
  const useForceUpdate = () => {
    const [, setForceUpdate] = useState(false)
    return () => setForceUpdate((prev) => !prev)
  }
  const dispatch = useAppDispatch()
  const navigate = useTenantNavigate()
  const awsConnectState = useAppSelector((state) => state?.appState)
  const [isLoading, setIsLoading] = useState(isNil(props.data))
  const forceUpdate = useForceUpdate()
  const refetchData = useRef(0)
  const [rows, setRows] = useState<ListingTableRow[]>([])
  const [subtotalObject, setSubtotalObject] = useState<FieldValues>({})
  const [filter, setFilter] = useState<TableFilterObjectBody | null>(
    apiFilter?.current ?? {}
  )
  const rowPerPageRef = useRef(25)
  const [sortDirection, setSortDirection] = useState<
    | {
        keyName: string
        direction: SORT_KEYS
        isAsc: SORT_KEYS
        key: string
      }
    | undefined
  >(props.sortDirection)
  const [paginationData, setPaginationData] = useState({
    currentPage: 1,
    rowsPerPage: 25,
    totalPages: 1,
    totalElements: 1,
    numberOfElements: 1,
  })
  const entityObject = useAppSelector(
    (state) => state?.localCommon?.entityObject
  )
  const categoryData = useAppSelector(
    (state) => state?.categoryDropdown?.categoryMap
  )
  const { actions: defaultActions, onActionClick: onDefaultActionClick } =
    useListingTableActions(entityId, props.allLayouts, props.showDefaultActions)

  const preprocessExtraColumns = () => {
    const result = (props.extraColumns ?? []).map((col) => {
      const otherCol = {
        ...col,
        header: col.header ?? '',
        accessor: col.accessor ?? '',
        type: col.renderComponent
          ? columnTypesConst.COMPONENT
          : col.type ?? columnTypesConst.TEXT,
        componentName: col.componentName ?? '',
        minWidth: col.minWidth ?? '1',
        filterData: col.filterData,
        renderComponent: col.renderComponent,
        isVisibleInHeader: false,
        isSortable: col?.componentName !== componentNames.FILE_UPLOAD,
        isLookup: col.isLookup ?? false,
        LookupConfig: col.LookupConfig ?? [],
        is_hidden: col.is_hidden ?? false,
      }
      if (otherCol.type === columnTypesConst.CHECKBOX && otherCol.onClick)
        otherCol.isVisibleInHeader = true

      return otherCol
    })

    return result
  }

  const handleStringFilter = props.showStringFilterOptions
    ? (criteria: SearchCriteria, targetId?: number, field?: string) =>
        checkFilterColumn(entityName ?? '', criteria.search_key ?? '')
          ? wrappedGetStringOptionsForEntities(
              criteria,
              targetId,
              field,
              entityName,
              props.dataId,
              props.relationIdentifier
            )
          : getStringFilterOptions(criteria)
    : undefined

  const otherColumns = useRef<
    ({
      onClick?: NonNullable<typeof props.extraColumns>[0]['onClick']
      isVisibleInHeader: boolean
    } & (typeof cols)[0])[]
  >([])
  const cols = useMemo(() => {
    const columns = transformLayoutToCols(
      layout,
      entityId,
      entityName,
      props?.preventRedirect,
      props.dataId,
      props.relationIdentifier,
      handleStringFilter,
      props.isSortable,
      props?.disableOnFocusForColumn
    )
    if (props.hideFilters)
      columns.forEach((col) => (col.filterData = undefined))
    columns.forEach(
      (col) =>
        (col.filterData =
          col.componentName === componentNames.MULTI_LOOKUP
            ? undefined
            : col.filterData)
    )

    otherColumns.current = preprocessExtraColumns()

    return columns
  }, [props.extraColumns, categoryData])

  const getStringFilterOptions = async (searchCriteria: SearchCriteria) => {
    try {
      const payload = {
        entity_id: entityId.toString(),
        select: [searchCriteria.search_key],
        page: 0,
        size: 500,
        distinct: true,
        filters: [
          {
            criteria: [
              {
                field_name: searchCriteria.search_key,
                operation: searchCriteria.operator,
                values: searchCriteria.search_value,
              },
            ],
          },
          {
            criteria: [
              {
                field_name: searchCriteria.search_key,
                operation: 'LIKE',
                values: [],
                direction: 'ASC',
              },
            ],
          },
        ],
        layout_id: layout.id ?? -1,
      }
      const response = await getEntityDataByEntityIdApiForFilter({
        payload: payload as EntityDataByEntityFilterPayload,
        entityName: entityName,
      })
      return response?.data?.content?.map(
        (item: { [key: string]: string }) => item[searchCriteria.search_key]
      )
    } catch (err) {
      console.error(err)
      return []
    }
  }

  const formatRecords = (
    records: ListingTableRow[],
    colsToIgnore: string[]
  ) => {
    const prevValues = cloneDeep(records)
    const result = formatDataForListView(
      records,
      layout.layout_data
    ) as ListingTableRow[]

    result.forEach((row, i) => {
      colsToIgnore.forEach((key) => {
        row[key] = prevValues[i][key]
      })
    })
    return result
  }

  const transformRecordsToRows = (records: ListingTableRow[]) => {
    const colsToIgnore = [
      'id',
      ...otherColumns.current.map((col) => col.accessor),
    ]

    const result = formatRecords(records, colsToIgnore)
    records.forEach((row) => {
      hideUnauthorizedData(
        row,
        layout.layout_data.added_fields ?? {},
        entityId,
        true,
        colsToIgnore
      )
    })

    const typesToAddDraftText = [
      '',
      columnTypesConst.TEXT,
      columnTypesConst.HYPERLINK,
    ]
    result.forEach((row) => {
      row.__is_disabled__ = row.record_status === RECORD_STATUS.INACTIVE

      if (row.record_status === RECORD_STATUS.DRAFT)
        if (typesToAddDraftText.includes(cols[0].type))
          row[cols[0].accessor] = `(Draft) ${row[cols[0].accessor] ?? '--'}`
    })

    return result
  }
  const triggerFileDownload = async (url: string, fileName: string) => {
    try {
      dispatch(showLoader())
      const fetchUrl = await fetchDownloadUrlForSingleFIles(url, fileName)
      const response = await fetch(fetchUrl?.data)
      if (!response.ok) {
        throw new Error(NAMESPACES.DOWNLOAD_ERROR)
      }
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const aTag = document.createElement('a')
      aTag.href = blobUrl
      aTag.download = fileName
      document.body.appendChild(aTag)
      aTag.click()
      document.body.removeChild(aTag)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const openNewWindow = (path: string) => {
    const fromUrl = getTenantIdFromWindow()
    const fromRedux = store.getState().authUser.selectedClientTenant?.id
    const resolved =
      typeof fromUrl === 'number' && Number.isFinite(fromUrl)
        ? fromUrl
        : typeof fromRedux === 'number' && Number.isFinite(fromRedux)
        ? fromRedux
        : null
    const withTenant =
      resolved != null ? appendTenantIdToPath(path, resolved) : path
    openWithTenant(
      `${
        window.location.origin.endsWith('/')
          ? window.location.origin.slice(0, -1)
          : window.location.origin
      }${withTenant.startsWith('/') ? withTenant : `/${withTenant}`}`,
      '_blank'
    )
  }
  const fetchAllEntities = async () => {
    try {
      if (isEmpty(entityObject)) {
        const response = await getAllEntities()
        if (response?.data) {
          const entities = response?.data
          const caseEntity = entities.find(
            (entity: IEntityType) => entity.name === 'case'
          )
          if (caseEntity) {
            dispatch(
              setLocalEntityObject({
                case: {
                  name: caseEntity?.name,
                  entityId: caseEntity?.id,
                  type: caseEntity?.type,
                },
              })
            )
            return {
              name: caseEntity?.name,
              entityId: caseEntity?.id,
              type: caseEntity?.type,
            }
          }
        }
      }
    } catch (err) {
      console.error('error : ', err)
    }
  }

  const onHyperLinkClick = async (
    row: ListingTableRow,
    col: (typeof cols)[0],
    accessor: keyof ListingTableRow
  ) => {
    const removeDraft = (val: string | undefined | number | boolean) => {
      if (!val) return val
      return val.toString().replace(/^\(Draft\) /, '')
    }

    const value = removeDraft(row[accessor]?.toString())
    if (!value || value === '--') return
    if (col.accessor === ENTITY_IMPORTANT_KEYS.CASE_ID) {
      const caseEntity = !isEmpty(entityObject?.case)
        ? entityObject.case
        : await fetchAllEntities()
      if (!caseEntity) return

      const filterObject = {
        entity_name: caseEntity.name,
        select: [ENTITY_IMPORTANT_KEYS.ID, ENTITY_IMPORTANT_KEYS.CASE_KEY],
        filters: [
          {
            logical_operator: 'AND',
            criteria: [
              {
                values: [row?.case_id?.toString()],
                operation: 'EQUALS',
                field_name: ENTITY_IMPORTANT_KEYS.CASE_KEY,
              },
            ],
          },
        ],
      }

      const caseIdObject = await getGenericEntityListingAPI(filterObject)
      if (!isEmpty(caseIdObject)) {
        dispatch(showLoader())
        try {
          const migrated = await getMigrationStatus(caseIdObject[0]?.id)
          if (migrated) {
            const layoutId = await getMigratedLayoutId()
            navigate(`/entities/32/tab/${row.id}?layoutId=${layoutId}`)
          } else {
            openNewWindow(
              `/entity/${caseEntity.entityId}/workflow/review/${caseIdObject[0].id}`
            )
          }
        } catch (err) {
          console.error(err)
        } finally {
          dispatch(hideLoader())
        }
      }
    } else if (col.componentName === componentNames.UNIQUE_ID) {
      // TODO: Remove after verifying the case lower workflow link
      // if (entityName === ENTITY_NAME.CASE_LOWER) {
      //   await handleOpenLink(
      //     row?.id,
      //     entityId,
      //     row?.['services.id'] as number,
      //     false,
      //     navigate
      //   )
      //   return
      // } else
      if (
        entityName === ENTITY_NAME.CASE_LOWER ||
        entityName === ENTITY_NAME.CASE ||
        entityName === ENTITY_NAME.C_CASES
      ) {
        dispatch(showLoader())
        try {
          const migrated = await getMigrationStatus(row?.id)
          if (migrated) {
            const layoutId = await getMigratedLayoutId()
            navigate(`/entities/32/tab/${row?.id}?layoutId=${layoutId}`)
          } else {
            openNewWindow(`/entity/${entityId}/workflow/review/${row?.id}`)
          }
        } catch (err) {
          console.error(err)
        } finally {
          dispatch(hideLoader())
        }
        return
      }
      onDefaultActionClick(
        ACTION_ICONS_NAMESPACE.VIEW,
        row,
        props.openInNewTab,
        props.preventRedirect
      )
    } else if (col.componentName === componentNames.EMAIL) {
      openWithTenant(`mailto:${value}`, '_blank')
    } else if (col.componentName === componentNames.FILE_UPLOAD) {
      triggerFileDownload(
        row?.[accessor + '.aws_file_name'] as string,
        row?.[accessor + '.file_name'] as string
      )
    } else if (col.componentName === componentNames?.PHONE_NUMBER) {
      if (
        awsConnectState?.isLoggedInToAWS &&
        !awsConnectState?.dialPad?.callInProgress
      ) {
        const phoneNumber = removePhoneMasking(value as string)
        dispatch(setDialPad({ number: phoneNumber }))
      } else if (!awsConnectState?.isLoggedInToAWS) {
        dispatch(
          addToaster({
            message: NAMESPACES?.LOGIN_TO_CONTINUE,
            id: uuid(),
            type: 'error',
          })
        )
      } else if (awsConnectState?.dialPad?.callInProgress) {
        dispatch(
          addToaster({
            message: NAMESPACES?.CALL_IN_PROGRESS,
            id: uuid(),
            type: 'error',
          })
        )
      }
    } else if (col.componentName === componentNames.SINGLE_LOOKUP_CLICK) {
      navigate(
        `/entities/${ENTITY_NAME.CASE_ENTITY_ID}/view/${row?.['cases.id']}`
      )
    } else if (
      col.accessor === ENTITY_IMPORTANT_KEYS.TASK_ID &&
      col.type === 'hyperlink' &&
      (row?.['case_id.id'] || row?.['case_for_this_task.id']) &&
      row?.['task_id']
    ) {
      const caseId = row?.['case_id.id'] || row?.['case_for_this_task.id']
      openNewWindow(
        `/entity/${ENTITY_NAME.CASE_ENTITY_ID}/workflow/review/${caseId}?TASK_KEY=${row?.['task_id']}`
      )
    } else {
      openWithTenant(value as string, '_blank')
    }
  }

  const onColumnClick = (
    isHeader: boolean,
    accessor: keyof DataTypeStore,
    row?: ListingTableRow | null,
    rowIndex?: number | null
  ) => {
    const otherCol = otherColumns.current.find(
      (column) => column.accessor === accessor
    )
    if (isHeader && otherCol?.type === columnTypesConst.CHECKBOX) {
      const newVal = !getTableHeaderValues()?.[otherCol.accessor]

      rows.forEach((r, i) => {
        if (r[otherCol?.accessor] !== newVal) otherCol.onClick?.(accessor, r, i)
      })
      forceUpdate()
      return
    }
    if (otherCol && row) {
      otherCol.onClick?.(accessor, row, rowIndex ?? -1)
      forceUpdate()
      return
    }

    const col = cols.find((column) => column.accessor === accessor)

    if (!col || !row) return
    if (col.type === columnTypesConst.HYPERLINK)
      return onHyperLinkClick(row, col, accessor)
  }

  const onActionClick = (
    key: string,
    row: ListingTableRow,
    rowIndex: number
  ) => {
    props.setCurrStep?.((prev) => prev + 1)
    const extraAction = (props.extraActions ?? []).find(
      (action) => action.key === key
    )
    if (extraAction) extraAction.onClick?.(row, rowIndex)
    else onDefaultActionClick(key, row)
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      setSortDirection({
        keyName: isAsc === SORT_KEYS.Disabled ? '' : key,
        direction: isAsc,
        key: isAsc === SORT_KEYS.Disabled ? '' : key,
        isAsc: isAsc,
      })
    }
  }

  const updateRows = async (currPage: number, rowsPerPage: number) => {
    const fetchRecords = async () => {
      try {
        setIsLoading(true)

        if (props.data) return { content: cloneDeep(props.data), totalPages: 1 }
        related_entities = {}
        Object.entries(layout.layout_data?.added_fields ?? {}).forEach(
          ([fieldName, field]) => {
            if (
              field?.ui_data_type === componentNames.SINGLE_LOOKUP ||
              field?.ui_data_type === componentNames.CATEGORY
            )
              related_entities[fieldName] = {
                select: Object.keys(field.added_fields ?? {}),
              }
          }
        )
        const updatedFilter = apiFilter?.current ?? filter
        if (
          entityId === ENTITY_ID.COMMUNICATION &&
          isCaseView &&
          updatedFilter
        ) {
          if (Object.keys(updatedFilter).length === 0) {
            updatedFilter.fields = null
          }
          if (!updatedFilter.search_criteria) {
            updatedFilter.search_criteria = {}
          }
          if (caseId) {
            updatedFilter.search_criteria[COMMUNICATION_SELECT_FIELDS.CASES] = {
              search_key: COMMUNICATION_SELECT_FIELDS.CASES,
              operator: correspondenceLogsPage.EQUALS,
              search_value: [caseId],
            }
          }
        }
        formatFilter = updatedFilter
          ? (formatFilterConfig(updatedFilter, cols, 'Entity', {
              key: sortDirection?.key ?? '',
              direction: sortDirection?.direction?.toUpperCase() ?? '',
            }) as {
              filter_criteria: EntityLookupSearchCriteria[]
              related_entities: TableFilterLookupObjectBody
            })
          : { filter_criteria: [], related_entities: {} }

        Object.keys(formatFilter.related_entities).forEach((key) => {
          if (related_entities[key]) {
            formatFilter.related_entities[key].select =
              related_entities[key].select
          }
        })

        const conditionalFilters = [
          ...(props?.diffKey === CUSTOM_SEPERATOR.ADVANCE_SEARCH_SEPERATOR
            ? []
            : [
                {
                  direction: 'ASC',
                  field_name: 'record_status',
                  operation: 'LIKE',
                  values: [],
                },
              ]),
          ...(props?.persistTableName === ENTITY_NAME.TASK_ENTITY
            ? [
                {
                  direction: 'ASC',
                  field_name: 'show_on_ui',
                  operation: 'LIKE',
                  values: ['true'],
                },
              ]
            : []),
        ]

        if (conditionalFilters.length > 0) {
          formatFilter.filter_criteria.push({
            criteria: conditionalFilters,
          })
        }

        const payload = {
          entity_id: entityId.toString(),
          select: cols.map((col) => col.accessor),
          page: currPage,
          size: rowsPerPage,
          related_entities: {
            ...related_entities,
            ...formatFilter.related_entities,
          },
          filters: formatFilter.filter_criteria,
          layout_id: layout.id ?? -1,
          ...(!isEmpty(props?.elementMetaData?.function_parameters) ||
          !isEmpty(layout?.function_parameters)
            ? {
                function_parameters: props?.elementMetaData?.function_parameters
                  ? props?.elementMetaData?.function_parameters
                  : layout?.function_parameters,
              }
            : {}),
        }
        if (props.dataFetchCallback) {
          const res = await props.dataFetchCallback(
            payload as EntityDataByEntityFilterPayload
          )
          return cloneDeep(res ?? {})
        }
        const response = await getEntityDataByEntityIdLayoutIdApiForFilter({
          payload: payload as EntityDataByEntityFilterPayload,
          entityName: entityName,
        })
        return response.data ?? {}
      } catch (err) {
        console.error(err)
        if (props?.persistTableName === ENTITY_NAME.TASK_ENTITY) {
          const is504 =
            isAxiosError(err) &&
            (err.response?.status === 504 || err.code === 'ECONNABORTED')
          dispatch(
            addToaster({
              id: uniqueId(),
              message: is504
                ? NAMESPACES.TASK_RECORD_LIMIT_EXCEEDED
                : NAMESPACES.TASK_FETCH_ERROR,
              type: is504 ? ToasterType.WARNING : ToasterType.ERROR,
            })
          )
        }
        return {}
      } finally {
        setIsLoading(false)
      }
    }
    rowPerPageRef.current = rowsPerPage
    if (entityId === 0) return
    const recordsResponse = await fetchRecords()
    setPaginationData({
      currentPage:
        recordsResponse.pageable?.pageNumber !== undefined
          ? recordsResponse.pageable?.pageNumber + 1
          : currPage,
      rowsPerPage: rowsPerPage,

      totalPages: recordsResponse.totalPages ?? 1,
      totalElements: recordsResponse.totalElements ?? 1,
      numberOfElements: recordsResponse.numberOfElements ?? 1,
    })
    const newRows = transformRecordsToRows(recordsResponse.content ?? [])
    if (props?.showSubTotal && props.subTotalCols) {
      const inferNumericKeys = (): string[] => {
        const first = newRows[0]
        if (!first) return []
        const numericKeys: string[] = []
        const numericPattern =
          /^-?\s*\d{1,3}(,\d{3})*(\.\d+)?$|^-?\s*\d+(\.\d+)?$/
        for (const key in first) {
          if (!Object.prototype.hasOwnProperty.call(first, key)) continue
          if (key === 'id' || key.startsWith('__')) continue
          const value = first[key as keyof ListingTableRow]
          if (typeof value === 'number') {
            numericKeys.push(key)
          } else if (
            typeof value === 'string' &&
            numericPattern.test(value.trim())
          ) {
            numericKeys.push(key)
          }
        }
        return numericKeys
      }
      const userKeys = props?.subTotalCols
      const keysToSum: string[] = (
        Array.isArray(userKeys) && userKeys.length
          ? userKeys
          : ['amount', ...inferNumericKeys()]
      ).filter((v, i, arr) => arr.indexOf(v) === i) // unique

      const subTotal: FieldValues = {}
      if (keysToSum.length && newRows.length) {
        const sums: Record<string, number> = {}
        for (const k of keysToSum) sums[k] = 0

        for (const row of newRows) {
          for (const key of keysToSum) {
            const raw = row[key as keyof ListingTableRow]
            if (raw == null) continue
            if (typeof raw === 'number') {
              sums[key] += raw
            } else if (typeof raw === 'string') {
              const cleaned = raw.trim().replace(/,/g, '')
              if (cleaned && !isNaN(Number(cleaned)))
                sums[key] += Number(cleaned)
            }
          }
        }
        for (const k of Object.keys(sums)) {
          const existing = subTotal[k]
          subTotal[k] =
            typeof existing === 'number' ? existing + sums[k] : sums[k]
        }
      }
      if (!newRows.length) {
        for (const key of keysToSum) {
          subTotal[key] = 0
        }
      }
      for (const key of keysToSum) {
        subTotal[key] = Math.round(subTotal[key] * 100) / 100
      }
      setSubtotalObject(subTotal ?? {})
      const subtotalKey = props?.parentKey
        ? `${HYBRID_LAYOUT_CONSTANTS?.EXTRA_FIELDS}.${HYBRID_LAYOUT_CONSTANTS?.SUB_TOTAL_KEY}.${props?.parentKey}`
        : `${HYBRID_LAYOUT_CONSTANTS?.EXTRA_FIELDS}.${HYBRID_LAYOUT_CONSTANTS?.SUB_TOTAL_KEY}`
      props?.handleSubtotalChange?.(subtotalKey, subTotal)
    }

    setRows(newRows)

    return recordsResponse
  }

  const getTableHeaderValues = () => {
    const result: Record<string, boolean> = {}
    otherColumns.current.forEach((col) => {
      if (col.isVisibleInHeader)
        result[col.accessor] = rows.every((row) => row[col.accessor])
    })
    return isEmpty(result) ? undefined : result
  }
  const fetchCategoryDropdowns = () => {
    for (const key in layout.layout_data?.added_fields ?? {}) {
      const field: IEntityFieldType & {
        filter_criteria?: { category_type?: string }
      } = layout.layout_data?.added_fields?.[key] ?? {}
      if (field.ui_data_type === componentNames.CATEGORY)
        dispatch(getCategoryDropdownData(field.filter_criteria?.category_type))
    }
  }

  const hasNoData = () => {
    if (isLoading) return false
    if (rows.length !== 0) return false
    if (!isEmpty(filter?.search_criteria)) return false

    return true
  }

  const handleFilterChange = (value: TableFilterObjectBody) => {
    setFilter(value)
    //NOTE: executing filtering logic in case, ViewsDropdown is present to prevent multiple api calls
    if (setApiFilter) {
      setApiFilter(value)
      updateRows(0, rowPerPageRef?.current)
    }
  }

  useEffect(() => {
    if (!setApiFilter) return
    setFilter((_prev) => {
      return apiFilter?.current ?? null
    })
  }, [apiFilter?.current])

  useEffect(() => {
    refetchData.current += 1
    const newRows = transformRecordsToRows(props.data ?? [])
    setRows(newRows)
  }, [props.data, layout])

  useEffect(() => {
    if (props?.dataId) {
      updateRows(0, rowPerPageRef?.current)
    }
  }, [props?.dataId])

  useEffect(() => {
    fetchCategoryDropdowns()
  }, [])

  type UiDataMappingsType = Record<string, UiDataMapping>

  const buildUiDataMappings = <T,>(
    cols: T[],
    getKey: (col: T) => string | undefined,
    getLabel: (col: T) => string | undefined
  ): UiDataMappingsType =>
    cols.reduce<UiDataMappingsType>((acc, col) => {
      const key = getKey(col)
      const label = getLabel(col)

      if (!key || !label) return acc

      acc[key] = { alias: [label] }
      return acc
    }, {})

  const enrichRelatedEntities = (
    relatedEntities: RelatedEntityType,
    addedFields: FieldDataByFieldNameType,
    uiDataMappings: UiDataMappingsType
  ) => {
    const updatedEntities = structuredClone(relatedEntities)

    Object.keys(updatedEntities).forEach((key: string) => {
      const addedField = addedFields[key]
      if (
        !(
          addedField?.ui_data_type === 'single lookup' ||
          addedField?.ui_data_type === 'category'
        )
      )
        return
      if (!uiDataMappings[key]) return

      const fieldMappings =
        addedField?.selected_fields?.reduce<UiDataMappingsType>(
          (acc, field) => {
            if (!field.field_name) return acc

            acc[field.field_name] = { alias: [addedField?.label || ''] }
            return acc
          },
          {}
        ) ?? {}

      updatedEntities[key] = {
        ...(updatedEntities[key] ?? {}),
        ui_data_mappings: fieldMappings,
        exclude_fields: ['record_id', 'entity_type', 'category'],
      }
    })

    return updatedEntities
  }

  const onExportClick = useCallback(async () => {
    if (!layout?.layout_data) return
    const isTableView = Boolean(tableColumns)

    const columns: Column[] = isTableView
      ? JSON.parse(tableColumns).filter(
          (col: { isHidden: boolean }) => !col.isHidden
        )
      : layout.layout_data.pages?.[0].rows?.[0].columns?.[0].elements ?? []

    if (!columns.length) return

    const uiDataMappings = buildUiDataMappings<Column>(
      columns,
      (col: Column) => {
        if ('accessor' in col) return col.accessor
        if ('keyName' in col) return col.keyName
        return undefined
      },
      (col: Column): string | undefined => {
        if ('header' in col) return col.header
        if ('meta' in col) return col.meta?.label
        return undefined
      }
    )

    const relatedEntitiesUpdated = enrichRelatedEntities(
      { ...related_entities, ...formatFilter.related_entities },
      layout.layout_data.added_fields || {},
      uiDataMappings
    )

    const payload: ExportPayloadType = {
      entity_name:
        entityName || (isTableView ? entityName : layout.entity_name),
      filters: formatFilter.filter_criteria,
      select: columns.map((col: Column) =>
        'accessor' in col
          ? col.accessor
          : ('keyName' in col && col.keyName) || ''
      ),
      related_entities: relatedEntitiesUpdated,
      ui_data_mappings: uiDataMappings,
      exclude_fields: ['record_id', 'entity_type', 'category'],
    }
    dispatch(showLoader())
    try {
      const { data } = await exportEntityDataByEntityIdLayoutIdApiForFilter(
        payload
      )

      downloadCSV(
        data,
        `${startCase(
          entityName || (isTableView ? entityName : layout.entity_name)
        )}${moment.utc(Date.now()).format('YYYYMMDD_HHmmss[Z]')}.csv`
      )
      dispatch(
        addToaster({
          id: uniqueId(),
          message: NAMESPACES.EXPORT_SUCCESSFUL,
          type: 'success',
        })
      )
    } catch (error: unknown) {
      let message = NAMESPACES.EXPORT_ERROR
      let type: ToasterTypeValues = ToasterType.ERROR

      if (isAxiosError(error)) {
        const backendMessage = error.response?.data?.message
        const statusCode = error.response?.status

        if (
          statusCode === HTTP_STATUS_CONSTANTS.SERVER_ERROR ||
          statusCode === HTTP_STATUS_CONSTANTS.GATEWAY_TIMEOUT ||
          !statusCode
        ) {
          message = NAMESPACES.EXPORT_RECORD_LIMIT_EXCEEDED
          type = ToasterType.WARNING
        } else if (
          statusCode === HTTP_STATUS_CONSTANTS.CONFLICT &&
          backendMessage
        ) {
          const parsed = parseErrorMessage(backendMessage)

          if (parsed.type === NAMESPACES.WARN) {
            message = parsed.message
            type = ToasterType.WARNING
          }
        }
      } else {
        message = NAMESPACES.EXPORT_RECORD_LIMIT_EXCEEDED
        type = ToasterType.WARNING
      }
      dispatch(
        addToaster({
          id: uniqueId(),
          message,
          type,
        })
      )
      console.log(error)
    } finally {
      dispatch(hideLoader())
    }
  }, [tableColumns, entityName, layout, enrichRelatedEntities])

  useEffect(() => {
    window.addEventListener(ENTITY_TABLE_EXPORT_EVENT, onExportClick)
    return () =>
      window.removeEventListener(ENTITY_TABLE_EXPORT_EVENT, onExportClick)
  }, [onExportClick])

  return (
    <>
      {/* <div className={`${isLoading ? '' : 'hidden'}`}>
        <SkeletonLoader />
      </div> */}
      <div
        className={hasNoData() ? 'hidden' : 'h-full flex flex-col'}
        id={'listing-table-modal-root'}
      >
        <div className="flex-1 min-h-0 flex">
          <DataContext.Provider
            value={{
              refetchData: () =>
                updateRows(
                  paginationData.currentPage - 1,
                  paginationData.rowsPerPage
                ),
            }}
          >
            <PersistTable
              tableName={`listing-table-${entityId}-${entityName}-${
                layout?.id
              }-${props?.persistTableName ?? ''}`}
              columns={
                (otherColumns.current.length
                  ? [
                      ...otherColumns.current,
                      ...cols.filter((col) => !col.is_hidden),
                      ...(props.endColumns ?? []),
                    ]
                  : [
                      ...cols.filter((col) => !col.is_hidden),
                      ...(props.endColumns ?? []),
                    ]) as TableColumnType<DataTypeStore>[]
              }
              data={rows}
              actions={
                props.haltActions
                  ? []
                  : props.extraActions?.length
                  ? [...defaultActions, ...props.extraActions]
                  : defaultActions
              }
              onActionClick={(
                key: string,
                row: DataTypeStore,
                rowIndex: number
              ) => onActionClick(key, row as ListingTableRow, rowIndex)}
              onColumnClick={(
                isHeader: boolean,
                accessor: keyof DataTypeStore,
                row?: DataTypeStore | null,
                rowIndex?: number | null
              ) =>
                onColumnClick(
                  isHeader,
                  accessor,
                  row as ListingTableRow,
                  rowIndex
                )
              }
              filterObject={filter ?? {}}
              onFilterChange={handleFilterChange}
              disabledKey={'__is_disabled__'}
              showTableColumnOnEmpty={true}
              headerValues={getTableHeaderValues()}
              tabIndex={tabIndex}
              onSortClick={onSortClick}
              sort={
                sortDirection?.key && !isEmpty(sortDirection?.key)
                  ? { key: sortDirection?.key, isAsc: sortDirection?.isAsc }
                  : null
              }
              isPersistColumns={!!props?.persistTableName}
              isColumnChooser={props?.showColumnChooser ?? false}
              showRestoreButton={props?.showRestoreButton}
              showSubTotal={props?.showSubTotal}
              subTotalCols={props?.subTotalCols}
              subtotalObject={subtotalObject}
              minHeight={isMinHeight ? MIN_TABLE_HEIGHT : ''}
              loading={isLoading}
            />
          </DataContext.Provider>
        </div>

        <PaginationWithFilter
          key={refetchData.current || 1}
          getAndUpdateData={updateRows}
          filter={filter ?? {}}
          refetch={props.refetchKey?.toString() ?? ''}
          sortDirection={sortDirection}
          hidePagination={!!props?.showSubTotal}
          hasViewDropdown={Boolean(setApiFilter)}
          paginationData={paginationData}
          setPaginationData={setPaginationData}
        />
      </div>
      <div className={`${hasNoData() ? '' : 'hidden'}`}>
        {props.noDataScreen?.(entityName ?? '') ?? (
          <NoDataScreen entityId={entityId} entityName={entityName} />
        )}
      </div>
    </>
  )
}

export default ListingTableFromLayout

interface EntityDataByEntityFilterPayload {
  entity_id: string
  select: string[]
  page: string | number
  size: string | number
  filters: { logical_operator?: string; criteria: formatDataObject[] }[]
  related_entities?:
    | Record<string, { select: string[] }>
    | Record<
        string,
        { select: string[]; filters: { criteria: formatDataObject[] }[] }
      >
  searchQuery?: string
  layout_id: number
  function_parameters?: FieldValues
}

export const NoDataScreen: React.FC<{
  entityId: number
  entityName: string
  noLayout?: boolean
}> = ({ entityId, entityName, noLayout }) => {
  const getText = () => {
    if (noLayout)
      return `No Layout present. Please Add it from Admin Portal and Ensure you're on correct client.`
    if (hasPermission(MODE_CONSTANTS.ADD, entityId ?? ''))
      return `No data present. Please add data.`
    return `No data present. You don't have permission to create ${entityName} records.`
  }
  return (
    <div className="flex flex-col items-center gap-2 justify-center border border-accent-100 p-2 rounded mt-2">
      <div className="font-primary font-light text-xs mt-6 mb-6">
        {getText()}
      </div>
    </div>
  )
}
